(() => {
  if (window.__vipPointScriptInitialized) {
    return;
  }

  window.__vipPointScriptInitialized = true;

  const SELECTORS = {
    container: '[data-cart-vip-points]',
    input: '[data-vip-point-input]',
    button: '[data-vip-point-apply]',
    buttonText: '[data-vip-point-button-text]',
    message: '[data-vip-point-message]',
    availablePoints:
      '[data-vip-available-points]',
    discountComponent:
      'cart-discount-component',
  };

  let sdkInstance = null;
  let isApplying = false;

  let approvedPointsRequest = null;
  let latestApprovedPoints = null;

  /**
   * VIP SDKを取得
   */
  const getSdk = () => {
    if (sdkInstance) {
      return sdkInstance;
    }

    if (
      typeof window.VipApp === 'undefined' ||
      typeof window.VipApp.VIPAppSDK !==
        'function'
    ) {
      throw new Error(
        'VIP SDKを読み込めませんでした。'
      );
    }

    sdkInstance =
      new window.VipApp.VIPAppSDK(
        window.location.origin
      );

    return sdkInstance;
  };

  const formatPoints = (points) => {
    return Number(points || 0).toLocaleString(
      'ja-JP'
    );
  };

  const getElements = (container) => {
    return {
      input: container.querySelector(
        SELECTORS.input
      ),
      button: container.querySelector(
        SELECTORS.button
      ),
      buttonText: container.querySelector(
        SELECTORS.buttonText
      ),
      message: container.querySelector(
        SELECTORS.message
      ),
      availablePointsElement:
        container.querySelector(
          SELECTORS.availablePoints
        ),
    };
  };

  const setMessage = (
    container,
    text = '',
    type = ''
  ) => {
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const { message } = getElements(container);

    if (!message) {
      return;
    }

    message.textContent = text;

    message.classList.remove(
      'is-error',
      'is-success'
    );

    if (type === 'error') {
      message.classList.add('is-error');
    }

    if (type === 'success') {
      message.classList.add('is-success');
    }
  };

  const setLoading = (
    container,
    loading
  ) => {
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const {
      input,
      button,
      buttonText,
    } = getElements(container);

    container.dataset.loading =
      String(loading);

    if (input) {
      input.disabled = loading;
    }

    if (button) {
      button.disabled = loading;

      button.setAttribute(
        'aria-busy',
        String(loading)
      );
    }

    if (buttonText) {
      buttonText.textContent = loading
        ? '適用中'
        : '適用';
    }
  };

  const updateAvailablePoints = (
    container,
    points
  ) => {
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const normalizedPoints = Math.max(
      0,
      Number(points) || 0
    );

    const {
      input,
      button,
      availablePointsElement,
    } = getElements(container);

    container.dataset.availablePoints =
      String(normalizedPoints);

    if (availablePointsElement) {
      availablePointsElement.textContent =
        formatPoints(normalizedPoints);
    }

    if (input) {
      input.max = String(normalizedPoints);

      const inputValue = Number(input.value);

      if (
        Number.isFinite(inputValue) &&
        inputValue > normalizedPoints
      ) {
        input.value = '';
      }
    }

    if (button && !isApplying) {
      button.disabled =
        normalizedPoints <= 0;
    }
  };

  /**
   * カートページとカートドロワーの
   * 所持ポイント表示を同期
   */
  const updateAllAvailablePoints = (
    points
  ) => {
    const normalizedPoints = Math.max(
      0,
      Number(points) || 0
    );

    latestApprovedPoints =
      normalizedPoints;

    document
      .querySelectorAll(SELECTORS.container)
      .forEach((container) => {
        updateAvailablePoints(
          container,
          normalizedPoints
        );
      });
  };

  /**
   * VIP側から最新ポイントを取得
   *
   * 同時に複数コンテナが初期化されても、
   * API通信は1回だけ実行します。
   */
  const requestApprovedPoints = async () => {
    if (approvedPointsRequest) {
      return approvedPointsRequest;
    }

    approvedPointsRequest = (async () => {
      const sdk = getSdk();

      const result =
        await sdk.customerApprovedPoint();

      return Math.max(
        0,
        Number(result?.approvedPoint) || 0
      );
    })();

    try {
      const points =
        await approvedPointsRequest;

      latestApprovedPoints = points;

      return points;
    } finally {
      approvedPointsRequest = null;
    }
  };

  const refreshAvailablePoints = async (
    container
  ) => {
    if (
      !(container instanceof HTMLElement) ||
      container.dataset.customerLoggedIn !==
        'true'
    ) {
      return;
    }

    try {
      if (
        latestApprovedPoints !== null
      ) {
        updateAvailablePoints(
          container,
          latestApprovedPoints
        );

        return;
      }

      const points =
        await requestApprovedPoints();

      updateAllAvailablePoints(points);
    } catch (error) {
      console.error(
        '[VIP] ポイント取得エラー:',
        error
      );
    }
  };

  const validatePoints = (container) => {
    const { input } = getElements(container);

    if (!input) {
      throw new Error(
        'ポイント入力欄が見つかりません。'
      );
    }

    const points = Number(input.value);

    const availablePoints = Number(
      container.dataset.availablePoints || 0
    );

    if (
      !Number.isInteger(points) ||
      points <= 0
    ) {
      throw new Error(
        '利用するポイントを1ポイント以上で入力してください。'
      );
    }

    if (points > availablePoints) {
      throw new Error(
        `利用可能なポイントは${formatPoints(
          availablePoints
        )}ポイントです。`
      );
    }

    return points;
  };

  /**
   * 対象カートと同じセクション内にある
   * cart-discount-componentを取得
   */
  const getDiscountComponent = (
    container
  ) => {
    const section = container.closest(
      '[id^="shopify-section-"]'
    );

    const sectionComponent =
      section?.querySelector(
        SELECTORS.discountComponent
      );

    if (
      sectionComponent &&
      typeof sectionComponent
        .applyDiscountCode === 'function'
    ) {
      return sectionComponent;
    }

    const fallbackComponent =
      document.querySelector(
        SELECTORS.discountComponent
      );

    if (
      fallbackComponent &&
      typeof fallbackComponent
        .applyDiscountCode === 'function'
    ) {
      return fallbackComponent;
    }

    return null;
  };

  const getErrorMessage = (error) => {
    const errorText = String(
      error?.message || error || ''
    );

    if (
      error?.response?.status === 429 ||
      error?.status === 429 ||
      errorText.includes('429') ||
      errorText.includes('Rate exceeded')
    ) {
      return '現在ポイント機能が混み合っています。時間を空けてから再度お試しください。';
    }

    return (
      errorText ||
      'ポイントを適用できませんでした。'
    );
  };

  /**
   * VIPポイントを利用
   */
  const applyPoints = async (
    originalContainer
  ) => {
    if (isApplying) {
      return;
    }

    let points;

    try {
      points = validatePoints(
        originalContainer
      );
    } catch (error) {
      setMessage(
        originalContainer,
        error.message,
        'error'
      );

      return;
    }

    const discountComponent =
      getDiscountComponent(
        originalContainer
      );

    if (!discountComponent) {
      setMessage(
        originalContainer,
        'カートの割引機能を読み込めませんでした。',
        'error'
      );

      return;
    }

    const sectionId =
      discountComponent.dataset.sectionId;

    const currentAvailablePoints = Number(
      originalContainer.dataset
        .availablePoints || 0
    );

    let activeContainer =
      originalContainer;

    isApplying = true;

    setLoading(activeContainer, true);
    setMessage(activeContainer, '');

    try {
      const sdk = getSdk();

      /**
       * VIP側でポイントを保留し、
       * 専用ディスカウントコードを発行
       */
      const vipResult =
        await sdk.customerPointUse({
          point: points,
        });

      if (
        !vipResult ||
        !vipResult.discountCode
      ) {
        throw new Error(
          'ポイント用ディスカウントコードを取得できませんでした。'
        );
      }

      /**
       * Discount.jsの共通処理から
       * Shopifyカートへ割引コードを適用
       */
      const discountResult =
        await discountComponent.applyDiscountCode(
          vipResult.discountCode,
          {
            showComponentErrors: false,
          }
        );

      if (!discountResult?.success) {
        if (
          discountResult?.reason ===
          'discount_code'
        ) {
          throw new Error(
            'ポイント用クーポンが現在のカートに適用できません。'
          );
        }

        if (
          discountResult?.reason ===
          'shipping'
        ) {
          throw new Error(
            'ポイント用クーポンを商品金額へ適用できません。'
          );
        }

        if (
          discountResult?.reason ===
          'aborted'
        ) {
          throw new Error(
            'ポイント適用処理が中断されました。'
          );
        }

        if (
          discountResult?.error instanceof
          Error
        ) {
          throw discountResult.error;
        }

        throw new Error(
          'ポイント用クーポンを適用できませんでした。'
        );
      }

      const appliedPoints = Math.max(
        0,
        Number(
          vipResult.point || points
        ) || 0
      );

      /**
       * morphSection後は元のcontainerが
       * 置き換えられている可能性があるため再取得
       */
      let freshContainer = null;

      if (
        typeof sectionId === 'string'
      ) {
        freshContainer =
          document
            .getElementById(
              `shopify-section-${sectionId}`
            )
            ?.querySelector(
              SELECTORS.container
            ) || null;
      }

      if (!freshContainer) {
        freshContainer =
          document.querySelector(
            SELECTORS.container
          );
      }

      if (freshContainer) {
        activeContainer = freshContainer;

        /**
         * MutationObserverによる再取得を防止
         */
        activeContainer.dataset.vipInitialized =
          'true';

        const updatedAvailablePoints =
          Math.max(
            0,
            currentAvailablePoints -
              appliedPoints
          );

        updateAllAvailablePoints(
          updatedAvailablePoints
        );

        const { input } =
          getElements(activeContainer);

        if (input) {
          input.value = '';
        }

        setMessage(
          activeContainer,
          `${formatPoints(
            appliedPoints
          )}ポイントを適用しました。`,
          'success'
        );
      }
    } catch (error) {
      console.error(
        '[VIP] ポイント適用エラー:',
        error
      );

      if (
        !activeContainer?.isConnected
      ) {
        activeContainer =
          document.querySelector(
            SELECTORS.container
          );
      }

      setMessage(
        activeContainer,
        getErrorMessage(error),
        'error'
      );
    } finally {
      isApplying = false;

      if (
        !activeContainer?.isConnected
      ) {
        activeContainer =
          document.querySelector(
            SELECTORS.container
          );
      }

      if (activeContainer) {
        setLoading(
          activeContainer,
          false
        );

        const availablePoints = Number(
          activeContainer.dataset
            .availablePoints || 0
        );

        const { button } =
          getElements(activeContainer);

        if (button) {
          button.disabled =
            availablePoints <= 0;
        }
      }
    }
  };

  /**
   * 新しく追加されたVIPコンテナを初期化
   */
  const initializeContainers = (
    root = document
  ) => {
    const containers = [];

    if (
      root instanceof HTMLElement &&
      root.matches(SELECTORS.container)
    ) {
      containers.push(root);
    }

    if (
      root instanceof Document ||
      root instanceof DocumentFragment ||
      root instanceof HTMLElement
    ) {
      containers.push(
        ...root.querySelectorAll(
          SELECTORS.container
        )
      );
    }

    containers.forEach((container) => {
      if (
        container.dataset.vipInitialized ===
        'true'
      ) {
        return;
      }

      container.dataset.vipInitialized =
        'true';

      refreshAvailablePoints(container);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      if (
        !(event.target instanceof Element)
      ) {
        return;
      }

      const button = event.target.closest(
        SELECTORS.button
      );

      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const container = button.closest(
        SELECTORS.container
      );

      if (!container) {
        return;
      }

      applyPoints(container);
    }
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key !== 'Enter' ||
        !(event.target instanceof Element)
      ) {
        return;
      }

      const input = event.target.closest(
        SELECTORS.input
      );

      if (!input) {
        return;
      }

      const container = input.closest(
        SELECTORS.container
      );

      if (!container) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      applyPoints(container);
    }
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeContainers();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeContainers(event.target);
    }
  );

  if (
    document.readyState !== 'loading'
  ) {
    initializeContainers();
  }

  const observer = new MutationObserver(
    (mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(
          (node) => {
            if (
              node instanceof HTMLElement
            ) {
              initializeContainers(node);
            }
          }
        );
      });
    }
  );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true,
    }
  );
})();